import type { Extensions } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { IndentClass } from './indentClass';
import { PlaceholderNode } from './placeholderNode';
import { TEMPLATE_TABLE_EXTENSIONS } from './tableExtensions';
import { TextAlignClass } from './textAlignClass';

export interface RichTextLinkPolicy {
    isAllowed: (url: string) => boolean;
    message: string;
}

export const DEFAULT_LINK_POLICY: RichTextLinkPolicy = {
    isAllowed: (url) => /^(https:\/\/|mailto:)/i.test(url),
    message: 'Enter an https:// or mailto: address',
};

export const RICH_TEXT_FEATURES = ['marks', 'headings', 'lists', 'indent', 'alignment', 'tables', 'link', 'history'] as const;

export type RichTextFeature = (typeof RICH_TEXT_FEATURES)[number];

export interface RichTextConfig {
    features: readonly RichTextFeature[];
    linkPolicy: RichTextLinkPolicy;
    placeholderPattern?: RegExp;
}

export const DEFAULT_RICH_TEXT_CONFIG: RichTextConfig = {
    features: RICH_TEXT_FEATURES,
    linkPolicy: DEFAULT_LINK_POLICY,
};

const extensionsByConfig = new WeakMap<RichTextConfig, Extensions>();

export function richTextExtensions(config: RichTextConfig = DEFAULT_RICH_TEXT_CONFIG): Extensions {
    const cached = extensionsByConfig.get(config);
    if (cached) return cached;
    const has = (feature: RichTextFeature) => config.features.includes(feature);
    const marks = has('marks') ? {} : false;
    const lists = has('lists') ? {} : false;
    const extensions: Extensions = [
        StarterKit.configure({
            trailingNode: false,
            bold: marks,
            italic: marks,
            underline: marks,
            strike: marks,
            code: marks,
            heading: has('headings') ? {} : false,
            bulletList: lists,
            orderedList: lists,
            listItem: lists,
            listKeymap: lists,
            link: has('link')
                ? {
                      autolink: false,
                      linkOnPaste: false,
                      openOnClick: false,
                      HTMLAttributes: { target: null, rel: null, class: null },
                      isAllowedUri: (url) => config.linkPolicy.isAllowed(url),
                  }
                : false,
        }),
        ...(has('alignment') ? [TextAlignClass] : []),
        ...(has('indent') ? [IndentClass] : []),
        ...(has('tables') ? TEMPLATE_TABLE_EXTENSIONS : []),
        ...(config.placeholderPattern ? [PlaceholderNode.configure({ pattern: config.placeholderPattern })] : []),
    ];
    extensionsByConfig.set(config, extensions);
    return extensions;
}
